const THEME_STORAGE_KEY = 'site-theme-mode';
const THEME_COOKIE_KEY = 'darkmode';
const TOGGLE_SELECTOR = '[data-darkmode-toggle]';
const VALID_THEMES = new Set(['light', 'dark']);

const normalizeTheme = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const cleaned = value.trim().toLowerCase();
  return VALID_THEMES.has(cleaned) ? cleaned : null;
};

const canUseStorage = () => {
  try {
    if (!window.localStorage) {
      return false;
    }

    const probeKey = '__darkmode_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);

    return true;
  } catch (error) {
    return false;
  }
};

const hasStorage = canUseStorage();

const getStoredTheme = () => {
  if (!hasStorage) {
    return null;
  }

  return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
};

const setStoredTheme = (theme) => {
  if (!hasStorage) {
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
};

const getCookieValue = (name) => {
  if (!document.cookie) {
    return null;
  }

  const entries = document.cookie.split(';');

  for (const entry of entries) {
    const [rawKey, ...rest] = entry.trim().split('=');

    if (rawKey !== name) {
      continue;
    }

    return decodeURIComponent(rest.join('='));
  }

  return null;
};

const getThemeFromCookie = () => {
  const cookieValue = getCookieValue(THEME_COOKIE_KEY);

  if (cookieValue === '1') {
    return 'dark';
  }

  if (cookieValue === '0') {
    return 'light';
  }

  return normalizeTheme(cookieValue);
};

const setThemeCookie = (theme) => {
  const isDark = theme === 'dark';
  const oneYearInSeconds = 60 * 60 * 24 * 365;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = `${THEME_COOKIE_KEY}=${isDark ? '1' : '0'}; path=/; max-age=${oneYearInSeconds}; SameSite=Lax${secure}`;
};

const getSystemTheme = () => {
  if (!window.matchMedia) {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getToggleElements = () => document.querySelectorAll(TOGGLE_SELECTOR);

const isDarkmodeDisabled = () => {
  if (window.SiteFeatures && window.SiteFeatures.darkmode === false) {
    return true;
  }

  return document.documentElement.getAttribute('data-darkmode') === 'off';
};

const syncToggleState = (theme) => {
  const isDark = theme === 'dark';

  getToggleElements().forEach((toggle) => {
    toggle.setAttribute('aria-pressed', String(isDark));
    toggle.setAttribute('data-theme', theme);
  });
};

const applyTheme = (theme, { persist = false } = {}) => {
  const normalizedTheme = normalizeTheme(theme) || 'light';
  const root = document.documentElement;
  const previousTheme = normalizeTheme(root.getAttribute('data-bs-theme'));

  root.setAttribute('data-bs-theme', normalizedTheme);
  root.style.colorScheme = normalizedTheme;
  syncToggleState(normalizedTheme);

  if (previousTheme !== normalizedTheme) {
    const eventDetail = { theme: normalizedTheme };
    document.dispatchEvent(new CustomEvent('site:theme-change', { detail: eventDetail }));
    window.dispatchEvent(new CustomEvent('site:theme-change', { detail: eventDetail }));
  }

  if (persist) {
    setStoredTheme(normalizedTheme);
    setThemeCookie(normalizedTheme);
  }
};

const initDarkmode = () => {
  if (isDarkmodeDisabled()) {
    return;
  }

  const hasToggle = getToggleElements().length > 0;
  const storedTheme = getStoredTheme();
  const cookieTheme = getThemeFromCookie();
  const hasSavedPreference = Boolean(storedTheme || cookieTheme);

  if (!hasToggle && !hasSavedPreference) {
    return;
  }

  const initialTheme = storedTheme || cookieTheme || getSystemTheme();
  applyTheme(initialTheme, { persist: false });

  getToggleElements().forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const currentTheme = document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
      const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
      applyTheme(nextTheme, { persist: true });
    });
  });

  if (!storedTheme && !cookieTheme && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event) => {
      applyTheme(event.matches ? 'dark' : 'light', { persist: false });
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleSystemThemeChange);
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDarkmode, { once: true });
} else {
  initDarkmode();
}
